import React, { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { 
  Users, 
  Shield, 
  Settings, 
  ArrowLeft,
  LayoutDashboard,
  List,
  LogOut,
  Building,
  Star
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function AdminLayout({ children }) {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const adminNavItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Admin Overview', testId: 'admin-nav-overview', exact: true },
    { path: '/admin/users', icon: Users, label: 'User Management', testId: 'admin-nav-users' },
    { path: '/admin/roles', icon: Shield, label: 'Roles & Permissions', testId: 'admin-nav-roles' },
    { path: '/admin/lookups', icon: List, label: 'Dropdown Config', testId: 'admin-nav-lookups' },
    { path: '/admin/firm-settings', icon: Building, label: 'Firm Settings', testId: 'admin-nav-firm-settings' },
    { path: '/admin/reviews', icon: Star, label: 'Review Metrics', testId: 'admin-nav-reviews' },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Admin Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 text-white z-50" data-testid="admin-sidebar">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Settings className="h-6 w-6 text-amber-400" />
            <h2 className="text-xl font-bold" data-testid="admin-sidebar-brand">Admin Panel</h2>
          </div>
          <p className="text-xs text-slate-400 mt-1" data-testid="admin-sidebar-user">{user?.email}</p>
        </div>

        {/* Back to App Link */}
        <div className="p-4 border-b border-slate-700">
          <Link to="/dashboard" data-testid="admin-back-to-app">
            <motion.div
              whileHover={{ x: -4 }}
              className="flex items-center space-x-3 px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-all"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm font-medium">Back to App</span>
            </motion.div>
          </Link>
        </div>

        <nav className="p-4 space-y-1">
          {adminNavItems.map((item) => {
            const active = isActive(item);
            return (
              <Link key={item.path} to={item.path} data-testid={item.testId}>
                <motion.div
                  whileHover={{ x: 4 }}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    active
                      ? 'bg-amber-500 text-slate-900'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </motion.div>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-start bg-transparent border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8 min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
}
